import {
  useContext,
  useEffect,
  useInsertionEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  type Location,
  UNSAFE_DataRouterStateContext,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  createOverlayEntryState,
  joinPath,
} from "../../../hooks/routedOverlays/routedOverlayUtils";
import { RoutedOverlayChainContext } from "../../../hooks/routedOverlays/RoutedOverlayChainContext";
import { useOverlayClose } from "../../../hooks/routedOverlays/useOverlayClose";
import {
  DrawerStackContext,
  type DrawerStackContextValue,
} from "./DrawerStackContext";
import { DrawerStackHost } from "./DrawerStackHost";
import {
  buildLevelPath,
  parseDrawerChain,
  validateDrawerRoutes,
} from "./drawerRoutes";
import { generateStackId } from "./LocalDrawerStackProvider";
import type {
  DrawerChain,
  DrawerRouteContainerProps,
  DrawerRouteDefinition,
  DrawerRouteLevel,
  DrawerStackEntry,
  StackedDrawerProps,
} from "./types";

interface RoutedDrawerStackProviderProps {
  children: ReactNode;
  maxDepth: number;
  routes: readonly DrawerRouteDefinition[];
  pathPrefix?: string;
  areRouteAncestorsReady?: (ancestors: readonly DrawerRouteLevel[]) => boolean;
}

interface MemoryStack {
  /** Pathname the entries were pushed at. They are dropped when the pathname changes. */
  pathname: string;
  entries: DrawerStackEntry[];
}

type DrawerStackActions = Omit<DrawerStackContextValue, "stack">;

const NO_ENTRIES: DrawerStackEntry[] = [];

const warnMaxDepth = (maxDepth: number) =>
  console.warn(
    `[DrawerStack] Maximum depth (${maxDepth}) reached. Cannot push more drawers.`,
  );

/** Keeps the token of each level still in the chain and gives new levels a fresh one. */
const nextGenerations = (
  previous: ReadonlyMap<string, symbol>,
  chain: DrawerChain,
): ReadonlyMap<string, symbol> =>
  new Map(
    chain.levels.map(({ path }) => [path, previous.get(path) ?? Symbol(path)]),
  );

/**
 * Drawer stack derived from the URL (see `DrawerStackProvider`): each stacked drawer pushed
 * with a registered `route` is a `{segment}/{id}[/{tab}]` level, so Back, Forward, refresh
 * and shared links restore it. Other entries stay in memory until the pathname changes.
 */
export function RoutedDrawerStackProvider({
  children,
  maxDepth,
  routes,
  pathPrefix,
  areRouteAncestorsReady,
}: RoutedDrawerStackProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const closeOverlay = useOverlayClose();

  const registry = useMemo(() => {
    validateDrawerRoutes(routes);
    return new Map(routes.map((route) => [route.segment, route]));
  }, [routes]);
  const chain = useMemo(
    () => parseDrawerChain(location.pathname, routes, maxDepth, pathPrefix),
    [location.pathname, routes, maxDepth, pathPrefix],
  );
  const stackedLevels = useMemo(
    () =>
      chain.levels.filter((level) => registry.get(level.segment)?.component),
    [chain, registry],
  );

  const [memory, setMemory] = useState<MemoryStack>({
    pathname: location.pathname,
    entries: NO_ENTRIES,
  });
  // Reset rather than hide, so Back then Forward does not bring the entries back.
  if (memory.pathname !== location.pathname && memory.entries.length > 0) {
    setMemory({ pathname: location.pathname, entries: NO_ENTRIES });
  }
  const memoryEntries =
    memory.pathname === location.pathname ? memory.entries : NO_ENTRIES;

  // The actions are stable and can run after their render (e.g. from a late onClose), so
  // they read the latest committed values. An insertion effect commits them before any
  // layout or passive effect (including children's) can call an action.
  const latest = {
    location,
    navigate,
    closeOverlay,
    chain,
    stackedLevels,
    registry,
    memoryEntries,
    maxDepth,
  };
  const latestRef = useRef(latest);
  useInsertionEffect(() => {
    latestRef.current = latest;
  });
  // Ignores navigations requested before the previous one has rendered.
  const pendingFromRef = useRef<Location | undefined>(undefined);
  // A data router blocker (e.g. a dirty dialog's discard prompt) can cancel that
  // navigation, which leaves the location unchanged; accept new actions once it resets.
  const blockers = useContext(UNSAFE_DataRouterStateContext)?.blockers;
  useEffect(() => {
    if (
      pendingFromRef.current === location &&
      blockers &&
      [...blockers.values()].every((blocker) => blocker.state === "unblocked")
    ) {
      pendingFromRef.current = undefined;
    }
  }, [blockers, location]);
  // The router's navigate keeps working after unmount, so a late call could leave the page.
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  // Props from in-app pushes, keyed by level path, so those levels render without a fetch.
  const cachedPropsRef = useRef(new Map<string, Record<string, unknown>>());
  // Entry ids passed to pushDrawer, so closeFrom accepts them as well as level paths.
  const levelPathByIdRef = useRef(new Map<string, string>());
  useEffect(() => {
    if (chain.levels.length === 0) {
      cachedPropsRef.current.clear();
      levelPathByIdRef.current.clear();
    }
  }, [chain]);

  const actions = useMemo<DrawerStackActions>(() => {
    const canNavigate = () =>
      !unmountedRef.current &&
      pendingFromRef.current !== latestRef.current.location;

    const closeTo = (path: string) => {
      pendingFromRef.current = latestRef.current.location;
      latestRef.current.closeOverlay(path);
    };

    const pushToMemory = (
      entry: Omit<DrawerStackEntry, "id"> & { id?: string },
    ) => {
      const current = latestRef.current;
      const { pathname } = current.location;
      const id = entry.id ?? generateStackId();
      setMemory((prev) => {
        const entries = prev.pathname === pathname ? prev.entries : NO_ENTRIES;
        if (current.stackedLevels.length + entries.length >= current.maxDepth) {
          warnMaxDepth(current.maxDepth);
          return prev;
        }
        if (entries.some((existing) => existing.id === id)) {
          return prev;
        }
        return { pathname, entries: [...entries, { ...entry, id }] };
      });
    };

    const closeFrom = (id: string) => {
      const current = latestRef.current;
      if (current.memoryEntries.some((entry) => entry.id === id)) {
        const { pathname } = current.location;
        setMemory((prev) => {
          const index =
            prev.pathname === pathname
              ? prev.entries.findIndex((entry) => entry.id === id)
              : -1;
          return index === -1
            ? prev
            : { pathname, entries: prev.entries.slice(0, index) };
        });
        return;
      }
      const levelPath = levelPathByIdRef.current.get(id) ?? id;
      const level = current.stackedLevels.find(
        (candidate) => candidate.path === levelPath,
      );
      if (level && canNavigate()) {
        closeTo(level.parentPath);
      }
    };

    return {
      pushDrawer: (entry) => {
        const current = latestRef.current;
        const { route } = entry;
        const definition = route
          ? current.registry.get(route.segment)
          : undefined;
        if (!route || !definition?.component) {
          if (route) {
            console.warn(
              `[DrawerStack] No stacked drawer is registered for "${route.segment}"; keeping it in memory.`,
            );
          }
          pushToMemory(entry);
          return;
        }
        const levelPath = buildLevelPath(
          current.chain.tipPath,
          route.segment,
          route.id,
        );
        if (levelPath === undefined) {
          console.warn(
            `[DrawerStack] Invalid id "${route.id}" for "${route.segment}"; not opening it.`,
          );
          return;
        }
        if (!canNavigate()) {
          return;
        }
        if (
          current.stackedLevels.length + current.memoryEntries.length >=
          current.maxDepth
        ) {
          warnMaxDepth(current.maxDepth);
          return;
        }
        const routeId = String(route.id);
        if (
          current.chain.levels.some(
            (level) => level.segment === route.segment && level.id === routeId,
          )
        ) {
          return;
        }
        let { tab } = route;
        if (tab !== undefined && !definition.tabs?.includes(tab)) {
          console.warn(
            `[DrawerStack] Tab "${tab}" is not registered for "${route.segment}"; opening without it.`,
          );
          tab = undefined;
        }
        cachedPropsRef.current.set(levelPath, entry.props);
        if (entry.id !== undefined) {
          levelPathByIdRef.current.set(entry.id, levelPath);
        }
        pendingFromRef.current = current.location;
        current.navigate(
          {
            pathname: joinPath(levelPath, tab),
            search: current.location.search,
          },
          { state: createOverlayEntryState(current.location) },
        );
      },
      popDrawer: () => {
        const current = latestRef.current;
        if (current.memoryEntries.length > 0) {
          const { pathname } = current.location;
          setMemory((prev) =>
            prev.pathname === pathname && prev.entries.length > 0
              ? { pathname, entries: prev.entries.slice(0, -1) }
              : prev,
          );
          return;
        }
        const top = current.stackedLevels[current.stackedLevels.length - 1];
        if (top && canNavigate()) {
          closeTo(top.parentPath);
        }
      },
      closeAll: () => {
        const current = latestRef.current;
        if (current.memoryEntries.length > 0) {
          setMemory((prev) =>
            prev.entries.length > 0
              ? { pathname: prev.pathname, entries: NO_ENTRIES }
              : prev,
          );
        }
        const [first] = current.stackedLevels;
        if (first && canNavigate()) {
          closeTo(first.parentPath);
        }
      },
      closeFrom,
    };
  }, []);

  // Each time a level enters the chain it gets a new generation token. Its onClose and
  // onNotFound are bound to that token, so a callback kept from a drawer that was dismissed
  // does not close a drawer later reopened at the same path. closeFrom(path) is not bound.
  const [generations, setGenerations] = useState(() => ({
    chain,
    byPath: nextGenerations(new Map(), chain),
  }));
  if (generations.chain !== chain) {
    setGenerations({
      chain,
      byPath: nextGenerations(generations.byPath, chain),
    });
  }
  const committedGenerationsRef = useRef(generations.byPath);
  useInsertionEffect(() => {
    committedGenerationsRef.current = generations.byPath;
  }, [generations]);
  const levelClosers = useMemo(
    () =>
      new Map(
        [...generations.byPath].map(([path, generation]) => [
          path,
          () => {
            if (committedGenerationsRef.current.get(path) === generation) {
              actions.closeFrom(path);
            }
          },
        ]),
      ),
    [generations, actions],
  );

  const stack = useMemo(() => {
    const routedEntries = chain.levels.flatMap(
      (level, index): DrawerStackEntry[] => {
        const component = registry.get(level.segment)?.component;
        if (!component) {
          return [];
        }
        return [
          {
            id: level.path,
            component,
            props: {
              routeId: level.id,
              tab: level.tab,
              levelPath: level.path,
              ancestors: chain.levels.slice(0, index),
              cachedProps: cachedPropsRef.current.get(level.path),
              onNotFound: levelClosers.get(level.path) ?? (() => {}),
            } satisfies Omit<
              DrawerRouteContainerProps,
              keyof StackedDrawerProps
            >,
            route: { segment: level.segment, id: level.id, tab: level.tab },
          },
        ];
      },
    );
    return memoryEntries.length > 0
      ? [...routedEntries, ...memoryEntries]
      : routedEntries;
  }, [chain, registry, memoryEntries, levelClosers]);

  const deferredIndices = useMemo(() => {
    if (!areRouteAncestorsReady) return undefined;
    const deferred = new Set<number>();
    stackedLevels.forEach((level, index) => {
      const chainIndex = chain.levels.indexOf(level);
      if (!areRouteAncestorsReady(chain.levels.slice(0, chainIndex))) {
        deferred.add(index);
      }
    });
    return deferred.size > 0 ? deferred : undefined;
  }, [areRouteAncestorsReady, chain, stackedLevels]);

  const contextValue = useMemo(() => ({ stack, ...actions }), [stack, actions]);

  return (
    <DrawerStackContext.Provider value={contextValue}>
      <RoutedOverlayChainContext.Provider value={chain}>
        {children}
        <DrawerStackHost
          closeHandlers={levelClosers}
          deferredIndices={deferredIndices}
        />
      </RoutedOverlayChainContext.Provider>
    </DrawerStackContext.Provider>
  );
}
